/**
 * StyleProvider component for managing CSS imports and theme configuration
 * Font loading is now handled in definition.ts
 */

import { useEffect } from "react";
import type { ColorMode } from "../types";
import {
  SUSTAINABILITY_CSS_CLASSES,
  SUSTAINABILITY_CSS_PATH,
  SUSTAINABILITY_ELEMENT,
} from "../../../../constants";
import { loadDocumentStylesheet } from "../utils/css-injection";

interface StyleProviderProps {
  assetsBaseUrl?: string;
  colorMode?: ColorMode;
  children: React.ReactNode;
}

export const StyleProvider = ({
  assetsBaseUrl,
  colorMode = "light",
  children,
}: StyleProviderProps) => {
  useEffect(() => {
    // Only inject CSS if assetsBaseUrl is provided
    if (!assetsBaseUrl) {
      return;
    }

    const componentUICss = "ComponentUI.css";
    const componentUIUrl = `${assetsBaseUrl}/${SUSTAINABILITY_CSS_PATH}/${componentUICss}`;

    const componentUIStyleSheet = loadDocumentStylesheet(
      componentUIUrl,
      componentUICss,
    );

    const isInShadowDOM = !!document.querySelector(
      SUSTAINABILITY_ELEMENT.tagName,
    )?.shadowRoot;
    let badgeStyleSheet: (() => void) | null = null;

    if (!isInShadowDOM) {
      const sustainabilityBadgeCss = "SustainabilityBadge.css";
      const sustainabilityBadgeUrl = `${assetsBaseUrl}/${SUSTAINABILITY_CSS_PATH}/${sustainabilityBadgeCss}`;
      badgeStyleSheet = loadDocumentStylesheet(
        sustainabilityBadgeUrl,
        sustainabilityBadgeCss,
      );
    }

    // Return cleanup function for both stylesheets
    return () => {
      componentUIStyleSheet?.();
      badgeStyleSheet?.();
    };
  }, [assetsBaseUrl]);

  return (
    <div className={`${SUSTAINABILITY_CSS_CLASSES.themePrefix}${colorMode}`}>
      {children}
    </div>
  );
};
